import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { Store, Clock, AlertCircle, Loader2, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { notifyParentModalOpen } from "@/lib/embed-modal";

type Props = {
  open: boolean;
  onClose: () => void;
  benefitDefinitionId: string | null;
  benefitTitle: string;
  onActivated?: () => void;
};

type Activation = { id: string; code: string; expiresAt: string };

function formatRemaining(ms: number): string {
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function BenefitActivationModal({
  open,
  onClose,
  benefitDefinitionId,
  benefitTitle,
  onActivated,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { staff, member } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activation, setActivation] = useState<Activation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [enteringTest, setEnteringTest] = useState(false);
  const [redeemed, setRedeemed] = useState<{ at: string; storeName: string | null } | null>(null);

  const staffNoMember = !!staff && !member;

  async function startTestMode() {
    if (enteringTest) return;
    setEnteringTest(true);
    try {
      const { data, error } = await supabase.functions.invoke("enter-test-mode", { body: {} });
      if (error || !data?.access_token || !data?.refresh_token) {
        throw new Error(error?.message || data?.error || "Kunde inte starta testläge");
      }
      const { error: sessErr } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (sessErr) throw sessErr;
      toast.success("Testläge aktivt — försök aktivera förmånen igen");
      onClose();
      navigate("/access/formaner");
    } catch (e) {
      toast.error((e as Error).message);
      setEnteringTest(false);
    }
  }


  useEffect(() => {
    if (open) notifyParentModalOpen();
    if (!open) {
      setActivation(null);
      setError(null);
      setLoading(false);
      setRedeemed(null);
      return;
    }
    if (!benefitDefinitionId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: rpcError } = await supabase.rpc("activate_benefit" as any, {
        _benefit_definition_id: benefitDefinitionId,
      });
      if (cancelled) return;
      setLoading(false);
      if (rpcError) {
        setError(rpcError.message || "Något gick fel");
        return;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (row?.activation_code) {
        setActivation({ id: row.log_id, code: row.activation_code, expiresAt: row.expires_at });
        onActivated?.();
      } else {
        setError("Kunde inte aktivera förmånen");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, benefitDefinitionId]);

  useEffect(() => {
    if (!activation) return;
    const tick = () => {
      const ms = new Date(activation.expiresAt).getTime() - Date.now();
      setRemainingMs(ms);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activation]);

  // Polla efter inlösen från ÅF-portalen — när redeemed_at sätts visar vi
  // "Godkänd" och stänger sedan automatiskt så koden inte ligger kvar.
  useEffect(() => {
    if (!activation || redeemed) return;
    let cancelled = false;
    const check = async () => {
      const { data } = await supabase
        .from("benefit_logs")
        .select("redeemed_at, stores:store_id(name)")
        .eq("id", activation.id)
        .maybeSingle();
      if (cancelled) return;
      if (data?.redeemed_at) {
        const storeName = (data as any).stores?.name ?? null;
        setRedeemed({ at: data.redeemed_at as string, storeName });
        queryClient.invalidateQueries({ queryKey: ["benefit_logs_month"] });
        queryClient.invalidateQueries({ queryKey: ["member_benefit_logs"] });
        onActivated?.();
      }
    };
    check();
    const id = setInterval(check, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activation, redeemed, onActivated, queryClient]);

  // Auto-stäng efter inlösen — användaren hinner se bekräftelsen.
  useEffect(() => {
    if (!redeemed) return;
    const t = setTimeout(() => onClose(), 6000);
    return () => clearTimeout(t);
  }, [redeemed, onClose]);

  const expired = activation && remainingMs <= 0 && !redeemed;



  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store size={18} className="text-primary" aria-hidden="true" />
            {benefitTitle}
          </DialogTitle>
          <DialogDescription>
            Visa denna kod för personalen i kassan. Koden är giltig i 15 minuter.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex flex-col items-center text-muted-foreground">
            <Loader2 className="animate-spin mb-3" size={28} aria-hidden="true" />
            <p className="text-sm">Skapar aktivering…</p>
          </div>
        )}

        {error && (
          <div className="py-6 flex items-start gap-3 rounded-lg bg-destructive/5 border border-destructive/20 p-4">
            <AlertCircle className="text-destructive shrink-0 mt-0.5" size={18} aria-hidden="true" />
            <div className="text-sm flex-1">
              <p className="font-display font-semibold text-destructive mb-1">Kan inte aktivera</p>
              <p className="text-muted-foreground">{error}</p>
              {staffNoMember && (
                <button
                  type="button"
                  onClick={startTestMode}
                  disabled={enteringTest}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--color-gold)]/15 border border-[var(--color-gold)]/40 text-[var(--color-gold)] hover:bg-[var(--color-gold)]/25 transition text-xs font-display tracking-wide disabled:opacity-60"
                >
                  <Eye size={13} /> {enteringTest ? "Startar testläge…" : "Starta testläge för att testa"}
                </button>
              )}
            </div>
          </div>
        )}

        {activation && !error && !redeemed && (
          <div className="py-4 text-center">
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground mb-3 font-display">
              <Clock size={12} aria-hidden="true" /> Giltig i {formatRemaining(remainingMs)}
            </div>
            <div
              className={`font-mono font-bold tabular-nums tracking-[0.25em] text-5xl sm:text-6xl py-6 rounded-2xl select-all ${
                expired
                  ? "bg-muted text-muted-foreground line-through"
                  : "bg-primary/10 text-primary"
              }`}
              aria-label={`Aktiveringskod ${activation.code.split("").join(" ")}`}
            >
              {activation.code}
            </div>
            {expired ? (
              <p className="mt-4 text-sm text-destructive font-display">
                Koden har gått ut. Stäng och aktivera igen vid behov.
              </p>
            ) : (
              <p className="mt-4 text-xs text-muted-foreground">
                Personalen knappar in koden i ÅF-portalen för att bekräfta din förmån.
              </p>
            )}
          </div>
        )}

        {redeemed && (
          <div className="py-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="text-emerald-600" size={32} aria-hidden="true" />
            </div>
            <p className="font-display font-bold text-xl mb-1">Godkänd!</p>
            <p className="text-sm text-muted-foreground">
              {redeemed.storeName
                ? `Inlöst hos ${redeemed.storeName}`
                : "Förmånen är inlöst i butik"}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {new Date(redeemed.at).toLocaleString("sv-SE", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
            </p>
            <p className="text-[11px] text-muted-foreground mt-4">
              Stängs automatiskt…
            </p>
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
